import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ProductTable from "./products/components/ProductTable";
import ProductGridView from "./products/components/ProductGridView";
import EditProduct from "./EditProduct";
import DeleteProducts from "./products/deleteproduct/DeleteProducts";
import EditMenu from "./EditMenu";
import AddProductModal from "./products/components/AddProductModal";
import ProductToolbar from "./products/components/ProductToolbar";
import { useProductHandlers } from "./products/hooks/useProductHandlers";
// @ts-ignore: allow side-effect CSS import without module declarations
import "./Edit.css";

import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/utils/useUserTheme";

interface ProductsPageProps {
  showEditMenu?: boolean;
}

const ProductsPage: React.FC<ProductsPageProps> = ({ showEditMenu = true }) => {
  const { user } = useUser();
  const { sidebarThemeClass } = useUserTheme();

  // URL query parameter
  const [searchParams, setSearchParams] = useSearchParams();

  const viewMode: "table" | "icon" = searchParams.get("view") === "icon" ? "icon" : "table";

  const handleViewModeChange = (mode: "table" | "icon") => {
    const params = new URLSearchParams(searchParams);
    params.set("view", mode);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    document.title = pageTitle("Products › Settings");
  }, []);

  const businessType = user?.businessType ?? "";

  const {
    products,
    categories,
    staff,
    totalRecords,
    loading,
    selectedProduct,
    showAddModal,
    showEditModal,
    showDeleteModal,
    searchTerm,
    categoryFilter,
    showDeletedProducts,
    page,
    rowsPerPage,
    refetchProducts,
    setShowAddModal,
    handleAddProduct,
    handleEditProduct,
    handleDeleteProduct,
    handleCloseModals,
    handleCategoryChange,
    handleToggleDeleted,
    handleSearchChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleReorderProduct,
  } = useProductHandlers({ businessType });

  const menu = { Product: "product" };

  return (
    <div className="p-1">
      {/*  1. Changed outer wrapper from overflow-hidden to overflow-visible so dropdowns aren't clipped. */}
      <div className={`flex flex-col w-full ${showEditMenu ? "h-[91vh]" : "h-auto"} shadow-inner rounded-lg items-center ${sidebarThemeClass} overflow-visible relative`}>
        {showEditMenu && <PageTitle title="- Settings › Products" />}
        {showEditMenu && <EditMenu editmenu={menu} />}

        <div className="w-full flex justify-center pt-2 py-2 px-2 overflow-visible relative">
          <ProductToolbar
            categories={categories}
            categoryFilter={categoryFilter}
            onCategoryChange={handleCategoryChange}
            showDeletedProducts={showDeletedProducts}
            onToggleDeleted={handleToggleDeleted}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onAddProduct={() => setShowAddModal(true)}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
          />
        </div>

        <div className="w-full flex justify-center p-2 flex-1 overflow-auto relative">
          {viewMode === "table" ? (
            <ProductTable
              products={products}
              categories={categories}
              loading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              totalRecords={totalRecords}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              showDeletedProducts={showDeletedProducts}
              onReorderProduct={categoryFilter !== "all" ? handleReorderProduct : undefined}
            />
          ) : (
            <ProductGridView
              products={products}
              categories={categories}
              showDeletedProducts={showDeletedProducts}
              page={page}
              rowsPerPage={rowsPerPage}
              totalRecords={totalRecords}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              onReorderProduct={categoryFilter !== "all" ? handleReorderProduct : undefined}
            />
          )}
        </div>

        <AddProductModal open={showAddModal} onClose={handleCloseModals} onSubmit={handleAddProduct} categories={categories} staffMembers={staff} businessType={businessType} />

        {showEditModal && selectedProduct && (
          <EditProduct
            open={showEditModal}
            product={selectedProduct}
            categories={categories}
            staffMembers={staff}
            businessType={businessType}
            onClose={handleCloseModals}
            onUpdate={refetchProducts}
            showReorderPosition={categoryFilter !== "all"}
          />
        )}

        {showDeleteModal && selectedProduct && <DeleteProducts product={selectedProduct} onClose={handleCloseModals} onDelete={refetchProducts} />}
      </div>

      {loading && <LoadingScreen />}
    </div>
  );
};

export default ProductsPage;